import {useEffect, useMemo, useState} from "react";
import Popup from "../../../../../components/general/Popup.tsx";
import {InfoTooltip} from "../../../../../components/general/InfoTooltip.tsx";
import MoneyInput from "../../../../../components/general/MoneyInput";
import Dropdown, {Option} from "../../../../../components/general/Dropdown.tsx";
import {Money, toCentsFromMoney, toMoneyFromCents} from "../../../../../lib/Money.ts";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {BankAccount} from "../entrepreneurSettingsPage/BankAccountsTable.tsx";
import {Institution} from "./AddCreditCardModal.tsx";

const BALANCE_TOOLTIP = 'Vul het saldo in dat je spaarrekening had aan het begin van 1 januari. Dit kun je terugvinden in je bank app.';

const EMPTY_INSTITUTION_OPTION: Option = {value: '', label: 'Selecteer bank'};

interface Props {
    open: boolean;
    relation: BankAccount | null;
    institutions: Institution[];
    onClose: () => void;
    onConfirm: (balanceCents: number, institutionName: string) => void | Promise<void>;
}

export function AddSavingsAccountBalanceModal({open, relation, institutions, onClose, onConfirm}: Props) {
    const [balance, setBalance] = useState<Money>(toMoneyFromCents(0, 'EUR'));
    const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {dispatch} = useNotifications();
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        if (open) {
            setBalance(toMoneyFromCents(0, 'EUR'));
            setSelectedInstitutionId('');
            setIsSubmitting(false);
        }
    }, [open]);

    const institutionOptions: Option[] = useMemo(
        () => [
            EMPTY_INSTITUTION_OPTION,
            ...institutions.map((institution) => ({
                value: institution.id,
                label: institution.name,
            })),
        ],
        [institutions],
    );

    const selectedInstitutionOption = institutionOptions.find(
        (option) => option.value === selectedInstitutionId,
    ) ?? EMPTY_INSTITUTION_OPTION;

    async function handleConfirm() {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onConfirm(toCentsFromMoney(balance), selectedInstitutionId);
        } catch {
            dispatch({
                title: 'Spaarrekening toevoegen mislukt',
                description: 'Er ging iets mis. Probeer het opnieuw.',
                type: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Popup
            open={open}
            title={'Spaarrekening toevoegen'}
            Icon={null}
            onClose={onClose}
            onConfirm={() => void handleConfirm()}
            confirmButtonDisplayLabel={'Toevoegen'}
            confirmButtonDisabled={isSubmitting || balance.signed_amount < 0 || selectedInstitutionId === ''}
            confirmButtonDisabledMessage={
                balance.signed_amount < 0
                    ? 'Het openingssaldo kan niet negatief zijn.'
                    : selectedInstitutionId === ''
                        ? 'Selecteer een bank.'
                        : undefined
            }
        >
            {relation && (
                <div className="mb-4">
                    <p>{relation.relation_name}</p>
                    <p className="font-mono text-gray-600">{relation.relation_iban}</p>
                </div>
            )}
            <label className="flex items-center gap-1 text-sm text-gray-700 mb-1">
                Saldo aan het begin van 1 januari {currentYear}
                <InfoTooltip text={BALANCE_TOOLTIP} position="right"/>
            </label>
            <MoneyInput value={balance} currency={'EUR'} onChange={setBalance}/>

            <label className="mt-4 mb-1 flex items-center gap-1 text-sm text-gray-700">
                Bank<span className="text-red-500">*</span>
                <InfoTooltip text="Selecteer de bank waar deze spaarrekening staat." position="right"/>
            </label>
            <Dropdown
                selected={selectedInstitutionOption}
                options={institutionOptions}
                onUpdateFunc={(value) => setSelectedInstitutionId(String(value))}
                customItemRenderFn={({value}) => {
                    const institution = institutions.find((item) => item.id === value);
                    if (!institution) {
                        return <span>{EMPTY_INSTITUTION_OPTION.label}</span>;
                    }
                    return (
                        <span className="flex items-center gap-2">
                            {institution.logo && (
                                <img
                                    src={institution.logo}
                                    alt=""
                                    className="h-5 w-5 rounded-full object-contain"
                                />
                            )}
                            <span>{institution.name}</span>
                        </span>
                    );
                }}
            />
        </Popup>
    );
}
